import { User } from '@/entities/user';
import type { CurrentUserInterface, OrgRole, PermissionString } from './types';
import type {
  CurrentUserNullable,
  PermissionSet,
  Shop,
  SubscriptionInfo,
  SubscriptionInfoFeaturesItem,
  UserRole,
  UserRoleRole,
} from '@/shared/api';

type CurrentUserApiType = NonNullable<CurrentUserNullable>;

/**
 * CurrentUser class - extends base User with authenticated user context
 * Represents the currently logged-in user with full context (shops, roles, onboarding, etc.)
 */
export class CurrentUser extends User implements CurrentUserInterface {
  // The authenticated session always carries these; base User declares them
  // optional to admit lean org/shop projections, so re-tighten them here.
  declare readonly avatarUrl: string | null;
  declare readonly phone: string;
  declare readonly role: UserRoleRole;
  readonly currentShopId: string | null;
  readonly emailVerified: boolean;
  readonly onboarding: {
    isCompleted: boolean;
    isDismissed: boolean;
  };
  readonly technician: {
    isActive: boolean;
    color: string | null;
  };
  readonly roles: UserRole[];
  readonly shops: Shop[];
  readonly permissions: PermissionSet;
  readonly subscription: SubscriptionInfo;
  readonly stripePublishableKey: string;
  readonly orgRole: OrgRole;
  readonly organizationId: string | null;
  readonly setupAnnouncementShowUntil: string | null;
  readonly setupPanelDismissedAt: string | null;

  constructor(data: CurrentUserApiType) {
    super(data); // Initialize base User fields
    this.currentShopId = data.currentShopId;
    this.emailVerified = data.emailVerified;
    this.onboarding = data.onboarding;
    this.technician = data.technician;
    this.roles = data.roles;
    this.shops = data.shops;
    this.permissions = data.permissions;
    this.subscription = data.subscription ?? {
      tier: 'free',
      features: ['basic_invoicing', 'customer_management'],
      expiresAt: null,
      stripeCustomerId: null,
      interval: null,
      freeRosAllowance: 3,
      freeRosUsed: 0,
      freeRosRemaining: 3,
      freeRosUnlimited: false,
      paymentMethodOnFile: false,
      cancelAtPeriodEnd: false,
    };
    this.stripePublishableKey = data.stripePublishableKey ?? '';
    this.orgRole = data.orgRole;
    this.organizationId = data.organizationId;
    this.setupAnnouncementShowUntil = data.setupAnnouncementShowUntil;
    this.setupPanelDismissedAt = data.setupPanelDismissedAt;
  }

  /**
   * User's primary shop (where they are admin)
   */
  get shop(): Shop | null {
    return this.shops.find((shop) => shop.adminUserId === this.id) ?? null;
  }

  /**
   * Whether the user's primary shop runs in demo/test mode
   */
  get isDemo(): boolean {
    return this.shop?.isDemo ?? false;
  }

  /**
   * Whether user has technician role
   */
  get isTechnician(): boolean {
    return this.roles.some((role) => role.role === 'technician');
  }

  /**
   * User's system-level role (role without shopId)
   * TODO: Get rid of this getter asap
   */
  get systemRole() {
    return this.roles.find((role) => role.shopId === null)?.role ?? null;
  }

  /**
   * User's shop-specific roles
   * TODO: Get rid of this getter asap
   */
  get shopRoles(): UserRole[] {
    return this.roles.filter((role) => role.shopId !== null);
  }

  /**
   * Whether user has admin role
   */
  get isAdmin(): boolean {
    return this.roles.some((role) => role.role === 'admin');
  }

  /**
   * Check if user has a specific permission
   * @param permission - Permission in dot notation (e.g., 'repairOrders.read')
   * @returns true if user has the permission
   */
  can(permission: PermissionString): boolean {
    const [resource, action] = permission.split('.');
    if (!resource || !action) {
      return false;
    }

    const resourcePermissions = this.permissions[resource as keyof PermissionSet];
    if (!resourcePermissions) {
      return false;
    }

    return (resourcePermissions as Record<string, boolean>)[action] === true;
  }

  /**
   * Check if user has a specific subscription feature
   */
  hasFeature(feature: SubscriptionInfoFeaturesItem): boolean {
    return this.subscription.features.includes(feature);
  }
}
